import {
  Button,
  Form,
  FormContent,
  Input,
  PopConfirm,
  Select,
  Table,
  TreeSelect,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { ColumnsType } from 'antd/es/table';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { getScadaListOwned } from '@/api/scada';
import { Space, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  apiV2AirStationConfigScadaListPost,
  apiV2AirStationConfigScadaUpdatePost,
  apiV2AreaListPost,
  V2AirStationConfigScadaListPostResponse,
} from '@maxtropy/device-customer-apis-v2';

const routes = [{ name: `监测站房配置` }];

interface DataType {
  id: React.Key;
  stationName: string;
  scadaName: string;
  scadaId: number;
}

export type StationItem = Exclude<V2AirStationConfigScadaListPostResponse['list'], undefined>[number];

const StationScadaConfig: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [editable, setEditable] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [prevList, setPrevList] = useState<StationItem[]>([]);
  const [stationTreeData, setStationTreeData] = useState<
    { key: number; value: number; title: string; children?: any[] }[]
  >([]);

  const { data: scadaList } = useRequest(() =>
    getScadaListOwned().then(res => {
      return res.list;
    })
  );

  // 监听表单中的 list 值，用于动态禁用已选择的站房
  const listValues = Form.useWatch('list', form);

  // 获取站房树形数据
  useEffect(() => {
    apiV2AreaListPost({}).then(res => {
      const transformToTreeSelectOptions = (nodes: any[]): any[] => {
        return nodes
          .filter((node: any) => node.id != null && node.zoneName != null)
          .map((node: any) => ({
            key: node.id,
            value: node.id,
            title: node.zoneName,
            children: node.children ? transformToTreeSelectOptions(node.children) : undefined,
          }));
      };
      const treeData = transformToTreeSelectOptions(res.list ?? []);
      setStationTreeData(treeData);
    });
  }, []);

  // 获取已配置的数据并回填
  useEffect(() => {
    apiV2AirStationConfigScadaListPost({}).then(res => {
      setPrevList(res.list ?? []);

      // 回填数据，使用 areaId
      form.setFieldsValue({
        list: (res.list ?? []).map(item => ({
          id: item.id,
          areaId: item.areaId,
          scada: {
            label: item.scadaName,
            value: item.scadaId,
          },
        })),
      });
    });
  }, []);

  const scadaOptions = useMemo(() => {
    return (
      scadaList?.map(item => ({
        label: item.name,
        value: item.id,
      })) || []
    );
  }, [scadaList]);

  // 根据 areaId 获取区域名称
  const getAreaNameById = (areaId: number): string | undefined => {
    const findNode = (nodes: any[]): string | undefined => {
      for (const node of nodes) {
        if (node.value === areaId) {
          return node.title;
        }
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
    };
    return findNode(stationTreeData);
  };

  const onFinish = (values: any) => {
    setLoading(true);
    apiV2AirStationConfigScadaUpdatePost({
      detail: (values.list ?? []).map((i: any) => ({
        id: prevList?.some(m => m.id === i.id) ? i.id : undefined,
        areaId: i.areaId,
        airStationName: i.areaId ? getAreaNameById(i.areaId) : undefined,
        scadaId: i.scada.value,
        scadaName: i.scada.label,
      })),
    })
      .then(res => {
        setEditable(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns: ColumnsType<DataType> = [
    {
      title: '选择站房',
      dataIndex: 'areaId',
      width: 200,
      render(_, record, index) {
        // 获取当前已选择的站房 id（排除当前行）
        const selectedStationIds = (listValues || [])
          .filter((item: any, i: number) => i !== index && item.areaId)
          .map((item: any) => item.areaId);

        // 禁用已选择的选项
        const disableNode = (treeData: any[]): any[] => {
          return treeData.map((node: any) => {
            const newNode: any = {
              ...node,
              disabled: selectedStationIds.includes(node.value),
            };
            if (node.children && node.children.length > 0) {
              newNode.children = disableNode(node.children);
            }
            return newNode;
          });
        };

        const treeDataWithDisabled = disableNode(stationTreeData);

        return (
          <Form.Item
            name={['list', index, 'areaId']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <TreeSelect
              placeholder="请选择"
              treeData={treeDataWithDisabled}
              showSearch
              treeDefaultExpandAll
              disabled={!editable}
              allowClear
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeNodeFilterProp="title"
              filterTreeNode={(input: string, node: any) => {
                const title = node.title || '';
                return title.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '组态名称',
      dataIndex: 'scadaName',
      width: 300,
      render(_, record, index) {
        return (
          <Form.Item
            name={['list', index, 'scada']}
            style={{ marginBottom: 0 }}
            rules={[{ message: '请选择', required: true }]}
          >
            <Select
              placeholder="请选择"
              options={scadaOptions}
              labelInValue
              showSearch
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              disabled={!editable}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'operation',
      width: 80,
      render: (_: any, _record: any, index: number) => (
        <PopConfirm
          placement="top"
          title="是否确认删除?"
          onConfirm={() => {
            const tableData = form.getFieldValue('list') ?? [];
            const newData = tableData.filter((_: any, i: number) => i !== index);
            form.setFieldsValue({ list: newData });
          }}
        >
          <Button type="link">删除</Button>
        </PopConfirm>
      ),
    },
  ];

  const addRow = () => {
    const tableData = form.getFieldValue('list') ?? [];
    const newRow = {
      id: uuidv4(),
      areaId: undefined,
      scada: undefined,
    };
    form.setFieldsValue({ list: [...tableData, newRow] });
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]}>
      <FormContent title="基本配置" style={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Form form={form} style={{ width: 700 }} isOrigin onFinish={onFinish} disabled={!editable}>
            <Form.Item noStyle labelAlign="left" name="list" valuePropName="dataSource">
              <Table columns={columns} rowKey="id" />
            </Form.Item>
            <Button wrapClassName={styles.addRowBtn} type="dashed" onClick={addRow} ghost icon={<PlusOutlined />}>
              新建
            </Button>
          </Form>
        </Spin>

        <Space size={8}>
          {editable ? (
            <>
              <Button onClick={() => setEditable(false)}>取消</Button>
              <Button type="primary" onClick={form.submit} disabled={loading}>
                保存
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  navigate('/station/scada/building');
                }}
              >
                返回
              </Button>
              <Button type="primary" onClick={() => setEditable(true)}>
                编辑
              </Button>
            </>
          )}
        </Space>
      </FormContent>
    </Wrapper>
  );
};

export default StationScadaConfig;
